import { useMemo } from 'react'
import { useIpUsage } from '@/api/queries'
import type { IpUsage } from '@/api/types'
import { DataTable, type Column, type FilterPreset } from '@/components/DataTable/DataTable'
import { Badge, ratioTone } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { compactNumber, megabytesToGb, percent } from '@/lib/format'

const errorRatio = (i: IpUsage) => (i.numberOfRequests ? (i.numberOfErrors / i.numberOfRequests) * 100 : 0)

export function IpUsageDialog({ onClose }: { onClose: () => void }) {
  const { data, isLoading, error } = useIpUsage(true)

  // One quick filter per provider; names with spaces cannot be expressed in the filter language.
  const presets = useMemo<FilterPreset[]>(
    () =>
      [...new Set((data ?? []).map((i) => i.provider).filter((p): p is string => !!p && !/\s/.test(p)))]
        .sort()
        .map((p) => ({ label: p, expression: `provider=${p}` })),
    [data],
  )

  const columns: Column<IpUsage>[] = [
    { key: 'ip', header: 'IP', render: (i) => <span className="u-mono">{i.ip}</span> },
    { key: 'provider', header: 'Provider', render: (i) => i.provider ?? '–' },
    { key: 'numberOfRequests', header: 'Requests', align: 'right', render: (i) => compactNumber(i.numberOfRequests) },
    { key: 'numberOfErrors', header: 'Errors', align: 'right', render: (i) => compactNumber(i.numberOfErrors) },
    {
      key: 'errorRatio',
      header: 'Error %',
      align: 'right',
      value: errorRatio,
      render: (i) => <Badge tone={ratioTone(errorRatio(i), { warnAbove: 5, dangerAbove: 20 })}>{percent(errorRatio(i), 1)}</Badge>,
    },
    { key: 'averageTimePerRequestMs', header: 'Avg ms', align: 'right', render: (i) => Math.round(i.averageTimePerRequestMs) },
    { key: 'sizeInMb', header: 'GB', align: 'right', render: (i) => megabytesToGb(i.sizeInMb) },
  ]

  return (
    <Modal title="IP usage · today" onClose={onClose} size="xl">
      <DataTable
        columns={columns}
        rows={data}
        rowKey={(i) => i.ip}
        loading={isLoading}
        error={error}
        searchable
        searchPlaceholder="Search an IP or provider"
        presets={presets}
        dense
        initialSort={{ key: 'numberOfRequests', direction: 'desc' }}
        copyText={(rows) => rows.map((r) => r.ip).join('\n')}
        emptyMessage="No proxy traffic recorded today"
      />
    </Modal>
  )
}
